import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressInboxV2MessagesServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/inbox/v2/messages',
        destPath: '/v2/messages',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/inbox-platform-messages',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_inbox_messages';

/**
 * Retrieves messages between the business and participant.
 *
 *
 * Up to 30 messages are returned per request. If the number of messages in a conversation is larger than 30,
 * `pagingMetadata.cursors` is returned in the response, indicating that another page of results is available.
 * To retrieve the next page of messages, pass the `next` cursor value
 * in the next request's `paging.cursor` parameter.
 *
 * To ensure you'll always retrieve the next record,
 * use the same visibility and sorting
 * in the first request and all subsequent requests.
 *
 * By default,
 * 30 messages are retrieved and sorted by `sequence` in descending order
 * (the most recent messages are first in the list).
 */
export function listMessages(payload: object): RequestOptionsFactory<any> {
  function __listMessages({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.crm.inbox.v2.message',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.inbox.v2.MessagesService.ListMessages',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressInboxV2MessagesServiceUrl({
        protoPath: '/v2/messages',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'messages.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __listMessages;
}

/**
 * Sends a message to the business or participant.
 *
 *
 * Specify the message type in `message.content`
 * by including the appropriate object: `basic`, `template`, `minimal`, or `form`.
 * For more information on message types
 * and how each type renders in Inbox and the Chat widget,
 * see [Message Types](#message-types).
 */
export function sendMessage(payload: object): RequestOptionsFactory<any> {
  function __sendMessage({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'message.createdDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.crm.inbox.v2.message',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.inbox.v2.MessagesService.SendMessage',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressInboxV2MessagesServiceUrl({
        protoPath: '/v2/messages',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'message.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __sendMessage;
}
